import React, { useState, useRef, useMemo } from 'react';
import { Box, Typography, Button, TextField, Paper, InputAdornment, IconButton, CircularProgress, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ImageIcon from '@mui/icons-material/Image';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import landingBg from '../assets/landing-page-bg.jpg';
import COUNTRIES from '../constants/countries';

const findCountryByCode = (code) => COUNTRIES.find((country) => country.code === code);
const findCountryByName = (name) => COUNTRIES.find(
    (country) => country.name.toLowerCase() === name.toLowerCase()
);

const baseInputSx = {
    '& .MuiOutlinedInput-root': {
        height: '56px',
        backgroundColor: 'white',
        borderRadius: 2,
        border: '1px solid rgba(0, 0, 0, 0.23)',
        '&:hover': {
            border: '1px solid rgba(0, 0, 0, 0.87)',
        },
        '&.Mui-focused': {
            border: '2px solid #1976d2',
        },
    },
    '& .MuiOutlinedInput-input': {
        paddingTop: '14px',
        paddingBottom: '14px',
        fontSize: '1rem',
    },
};

const selectInputSx = {
    ...baseInputSx,
    '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        paddingTop: '14px',
        paddingBottom: '14px',
        fontSize: '1rem',
        height: '100%',
        boxSizing: 'border-box',
    },
};

const zipInputWrapperSx = {
    position: 'relative',
    width: '100%',
};

const zipInputFieldBaseSx = {
    width: '100%',
    height: '56px',
    backgroundColor: 'white',
    borderRadius: 2,
    border: '1px solid rgba(0, 0, 0, 0.23)',
    fontSize: '1rem',
    padding: '0 16px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s ease, border-width 0.2s ease, padding 0.2s ease',
    '&:hover': {
        borderColor: 'rgba(0, 0, 0, 0.87)',
    },
    '&:focus': {
        borderColor: '#1976d2',
        borderWidth: '2px',
        padding: '0 15px',
    },
    '&::placeholder': {
        color: 'rgba(0, 0, 0, 0.6)',
    },
};

const Home = () => {
    const navigate = useNavigate();
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [zip, setZip] = useState('');
    const [searchText, setSearchText] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [countryCode, setCountryCode] = useState('US');
    const [locationPrompted, setLocationPrompted] = useState(false);
    const [locationPromptDisabled, setLocationPromptDisabled] = useState(false);
    const [isFetchingLocation, setIsFetchingLocation] = useState(false);
    const fileInputRef = useRef(null);
    const selectedCountry = useMemo(() => findCountryByCode(countryCode) || COUNTRIES[0], [countryCode]);
    const countryName = selectedCountry?.name || '';

    const fillLocationFromCoords = async (latitude, longitude) => {
        try {
            const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            if (!response.ok) {
                throw new Error('Reverse geocoding failed');
            }
            const data = await response.json();
            if (data.postcode) {
                setZip(data.postcode);
            }
            if (data.countryCode) {
                const normalizedCode = data.countryCode.toUpperCase();
                if (findCountryByCode(normalizedCode)) {
                    setCountryCode(normalizedCode);
                }
            } else if (data.countryName) {
                const matchedCountry = findCountryByName(data.countryName);
                if (matchedCountry) {
                    setCountryCode(matchedCountry.code);
                }
            }
        } catch (err) {
            console.error('Reverse geocoding error:', err);
            window.alert('We could not determine your location automatically. Please enter it manually.');
            setLocationPrompted(false);
        } finally {
            setIsFetchingLocation(false);
        }
    };

    const handleLocationPrompt = () => {
        if (locationPromptDisabled || locationPrompted || isFetchingLocation) {
            return;
        }
        setLocationPrompted(true);

        if (!navigator.geolocation) {
            window.alert('Geolocation is not supported by this browser.');
            setLocationPrompted(false);
            return;
        }

        const shouldUseLocation = window.confirm('Use your current location to auto-fill your ZIP code and country?');
        if (!shouldUseLocation) {
            setLocationPromptDisabled(true);
            setLocationPrompted(false);
            return;
        }

        setLocationPromptDisabled(true);
        setIsFetchingLocation(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                fillLocationFromCoords(position.coords.latitude, position.coords.longitude);
            },
            (geoError) => {
                console.error('Geolocation error:', geoError);
                window.alert('We could not access your location. Please enter it manually.');
                setIsFetchingLocation(false);
                setLocationPrompted(false);
                setLocationPromptDisabled(false);
            },
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 }
        );
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
            // Don't clear text search - allow both image and text
        }
    };

    const handleImageButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleSearch = (e) => {
        if (e) {
            e.preventDefault();
        }
        if (!zip || !countryCode) {
            setError('Please enter both a ZIP code and a country.');
            return;
        }
        if (!image && !searchText.trim()) {
            setError('Please upload an image or enter a search query.');
            return;
        }
        setError('');
        setLoading(true);
        navigate('/search', {
            state: {
                image,
                zip,
                country: countryName,
                countryCode,
                searchText: searchText.trim(),
                sessionName: searchText.trim() || 'Search' // Generate default name if needed
            }
        });
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: 'calc(100vh - 64px)',
                backgroundImage: `url(${landingBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', md: 'center' },
                overflow: 'hidden',
                pt: { xs: 10, sm: 12, md: 0 },
                pb: { xs: 6, md: 8 },
                boxSizing: 'border-box',
            }}
        >
            {/* Overlay for darkening the background for text readability */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: 'rgba(0,0,0,0.45)',
                    zIndex: 1,
                }}
            />
            {/* Main content */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    width: '100%',
                    maxWidth: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, sm: 3 },
                    py: { xs: 4, md: 0 },
                }}
            >
                <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                        color: 'white',
                        fontWeight: 700,
                        textAlign: 'center',
                        mb: 2,
                        mt: { xs: 2, md: 0 },
                        fontSize: { xs: '2.2rem', md: '3.5rem' },
                        textShadow: '0 2px 16px rgba(0,0,0,0.5)',
                    }}
                >
                    Meet Your Specs. Spend Less.
                </Typography>
                <Typography
                    variant="h5"
                    sx={{
                        color: 'white',
                        textAlign: 'center',
                        mb: 5,
                        fontWeight: 400,
                        textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    }}
                >
                    Explore options that meet your design and budget
                </Typography>
                {/* Search Form Box */}
                <Paper
                    elevation={0}
                    sx={{
                        px: { xs: 3, md: 4 },
                        py: { xs: 3, md: 4 },
                        borderRadius: 2,
                        width: { xs: '90%', sm: 400, md: 500 },
                        maxWidth: 600,
                        mx: 'auto',
                        mb: 4,
                        backgroundColor: 'white',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        display: 'flex',
                        flexDirection: 'column',
                        boxSizing: 'border-box',
                        boxShadow: {
                            xs: '0 12px 32px rgba(0,0,0,0.16)',
                            sm: '0 20px 48px rgba(0,0,0,0.2)',
                        },
                    }}
                >
                    <Box component="form" onSubmit={handleSearch} sx={{ width: '100%' }}>
                        {error && (
                            <Box sx={{ mb: 2 }}>
                                <Typography color="error">{error}</Typography>
                            </Box>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                mb: 2,
                                width: '100%'
                            }}
                        >
                            <Box sx={zipInputWrapperSx}>
                                <Box
                                    component="input"
                                    value={zip}
                                    onFocus={handleLocationPrompt}
                                    onChange={(e) => setZip(e.target.value)}
                                    placeholder="ZIP Code"
                                    autoComplete="postal-code"
                                    sx={{
                                        ...zipInputFieldBaseSx,
                                        paddingRight: isFetchingLocation ? '44px' : '16px',
                                    }}
                                />
                                {isFetchingLocation && (
                                    <CircularProgress
                                        size={20}
                                        sx={{
                                            position: 'absolute',
                                            right: 12,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                        }}
                                    />
                                )}
                            </Box>
                            <TextField
                                fullWidth
                                value={countryCode}
                                onFocus={handleLocationPrompt}
                                onChange={(e) => setCountryCode(e.target.value)}
                                placeholder="Country"
                                select
                                autoComplete="country-name"
                                sx={{ 
                                    ...selectInputSx,
                                }}
                                InputProps={{
                                    endAdornment: isFetchingLocation ? (
                                        <InputAdornment position="end">
                                            <CircularProgress size={20} />
                                        </InputAdornment>
                                    ) : null,
                                }}
                            >
                                {COUNTRIES.map((option) => (
                                    <MenuItem key={option.code} value={option.code}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        <Box sx={{ position: 'relative', width: '100%' }}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                            />
                            <TextField
                                fullWidth
                                value={searchText}
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                    // Don't clear image - allow both image and text
                                }}
                                placeholder="Paste a screenshot of an existing product to match, or describe what you're looking for"
                                multiline
                                minRows={2}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                        borderRadius: 2,
                                        minHeight: '80px',
                                        paddingBottom: '48px',
                                        paddingRight: '48px',
                                        paddingLeft: '14px',
                                        alignItems: 'flex-start',
                                        '& fieldset': {
                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                        },
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        paddingTop: '14px',
                                        paddingBottom: '14px',
                                        fontSize: '1rem',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment 
                                            position="end"
                                            sx={{
                                                position: 'absolute',
                                                right: 8,
                                                bottom: 8,
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            <IconButton
                                                onClick={handleSearch}
                                                disabled={loading || !zip || !countryCode || (!image && !searchText.trim())}
                                                sx={{
                                                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                                    borderRadius: '50%',
                                                    width: 40,
                                                    height: 40,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                                    },
                                                    '&:disabled': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                    },
                                                }}
                                            >
                                                <ArrowUpwardIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {/* Image button positioned at bottom left inside input */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    left: 14,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    pointerEvents: 'auto',
                                }}
                            >
                                <Button
                                    startIcon={<ImageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                                    onClick={handleImageButtonClick}
                                    sx={{
                                        color: 'text.secondary',
                                        textTransform: 'none',
                                        minWidth: 'auto',
                                        px: 1.5,
                                        py: 0.75,
                                        fontSize: '0.875rem',
                                        fontWeight: 400,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        borderRadius: 1,
                                        backgroundColor: 'white',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    Image
                                </Button>
                                {/* Image preview */}
                                {imagePreview && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            ml: 0.5,
                                        }}
                                    >
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{
                                                maxWidth: '48px',
                                                maxHeight: '48px',
                                                borderRadius: 4,
                                                objectFit: 'cover',
                                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default Home;
